import { CssBaseline } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { FC, PropsWithChildren, useEffect } from 'react'
import { CookiesProvider } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import { AuthProvider } from 'react-oidc-context'
import { APP_BASE_URL, OIDC_AUTHORITY, OIDC_CLIENT_ID } from '../api/endpoints.constants'
import { WebPushNotificationsClient } from './WebPushNotificationsProvider/webPushNotificationsClient'
import { WebPushNotificationsProvider } from './WebPushNotificationsProvider/WebPushNotificationsProvider'
import '../i18n/config'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { BrowserRouter } from 'react-router-dom'
import { theme } from '../theme'
import { SnackbarProvider } from './SnackbarProvider'

const urlParams = new URLSearchParams(window.location.search)
const assistant_id = urlParams.get('assistant_id')
const checkword = urlParams.get('checkword')
const workplaceId = urlParams.get('workplaceId')
const return_page = urlParams.get('rpg') || window.location.pathname

const params = []
if (assistant_id) params.push(`assistant_id=${assistant_id}`)
if (checkword) params.push(`checkword=${checkword}`)
if (workplaceId) params.push(`workplaceId=${workplaceId}`)
params.push(`rpg=${return_page}`)
const redirectTo = `login?${params.join('&')}`
const appBaseUrl = import.meta.env.DEV ? window.location.origin : APP_BASE_URL

const oidcConfig = {
    authority: OIDC_AUTHORITY,
    client_id: OIDC_CLIENT_ID,
    redirect_uri: `${appBaseUrl}/${redirectTo}`,
    scope: 'openid profile email',
    response_type: 'code',
    post_logout_redirect_uri: `${appBaseUrl}/login`,
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
        },
    },
})

const webPushClient = new WebPushNotificationsClient(
    'webPushServiceWorker.js',
    import.meta.env.VITE_VAPID_PUBLIC_KEY,
)

export const Providers: FC<PropsWithChildren> = ({ children }) => {
    const { i18n } = useTranslation()
    
    useEffect(() => {
        const locale = i18n.language || 'ru'
        dayjs.locale(locale)
    }, [i18n.language])
    
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter>
                <LocalizationProvider adapterLocale={i18n.language || 'ru'} dateAdapter={AdapterDayjs}>
                    <SnackbarProvider>
                        <AuthProvider {...oidcConfig}>
                            <CookiesProvider>
                                <QueryClientProvider client={queryClient}>
                                    <WebPushNotificationsProvider client={webPushClient}>
                                        {children}
                                    </WebPushNotificationsProvider>
                                </QueryClientProvider>
                            </CookiesProvider>
                        </AuthProvider>
                    </SnackbarProvider>
                </LocalizationProvider>
            </BrowserRouter>
        </ThemeProvider>
    )
}
